// 抽出ソース関連の型定義（openapi SourceFile / ExtractionDiff / ApplyExtraction スキーマ準拠）

import { parseOcrFailedReason } from '../../core/utils/ocr-failed-reason.utils';

import type { PaginatedResponse } from '../../core/models/common.models';
import type { OcrFailedReason } from '../../core/utils/ocr-failed-reason.utils';

// OcrFailedReason は SourceFile の型で使うため re-export（Issue #475 Phase 3）。
//
// 注意: 総括表（SourceFile）側の失敗理由は **まだ一覧に出していない**。
// 総括表の失敗はバナー（components/extraction-status-banner）で伝えており、
// その文言は Project 側の `extractionFailedReason` で出し分ける（Issue #475 Phase 5.5）。
// SourceFile 側の理由は同じ値を別経路で持っているだけなので、一覧に出す場合は
// Project 側との二重表示にならないか確認すること。
export type { OcrFailedReason };

// ========================================
// ExtractionStatus
// ========================================

/**
 * 抽出処理ステータス（openapi Project.extractionStatus 準拠）
 * idle: 未処理 / processing: 解析中 / ready_to_apply: 適用待ち / completed: 適用済 / failed: 失敗
 */
export type ExtractionStatus =
  | 'idle'
  | 'processing'
  | 'ready_to_apply'
  | 'completed'
  | 'failed';

// ========================================
// 差分（diff）関連
// ========================================

/**
 * 審査種別
 * employment: 雇用 / journal_entry: 仕訳帳
 */
export type ReviewType = 'employment' | 'journal_entry';

/**
 * 差分: 追加アイテム（openapi ExtractionDiffAddedItem 準拠）
 */
export type ExtractionDiffAddedItem = {
  employeeId: string;
  employeeName: string;
  reviewType: ReviewType;
  /** 対象月（省略可） */
  targetMonth?: string;
  /** 表示順（省略可） */
  displayOrder?: number;
};

/**
 * 差分: 更新アイテム（openapi ExtractionDiffUpdatedItem 準拠）
 */
export type ExtractionDiffUpdatedItem = {
  publicId: string;
  employeeId: string;
  reviewType: ReviewType;
  /** 対象月（省略可） */
  targetMonth?: string;
  before: {
    employeeName: string;
    /** 変更前の表示順（省略可） */
    displayOrder?: number;
  };
  after: {
    employeeName: string;
    /** 変更後の表示順（省略可） */
    displayOrder?: number;
  };
};

/**
 * 差分: 削除アイテム（openapi ExtractionDiffRemovedItem 準拠）
 */
export type ExtractionDiffRemovedItem = {
  publicId: string;
  employeeId: string;
  employeeName: string;
  reviewType: ReviewType;
  /** 対象月（省略可） */
  targetMonth?: string;
};

/**
 * 差分警告（openapi ExtractionWarning oneOf 準拠）
 * DuplicateEmployeeIdMerged: 社員ID重複のため統合
 * ConflictingEmployeeName: 氏名不一致
 * DuplicateTargetMonthsRemoved: 対象月重複のため削除
 */
export type ExtractionWarning =
  | {
      type: 'DuplicateEmployeeIdMerged';
      employeeId: string;
      sourceCount: number;
    }
  | { type: 'ConflictingEmployeeName'; employeeId: string; names: string[] }
  | {
      type: 'DuplicateTargetMonthsRemoved';
      employeeId: string;
      removed: number;
    };

/**
 * GET .../extraction-sources/diff レスポンス（openapi ExtractionDiffResponse 準拠）
 */
export type ExtractionDiffApiResponse = {
  added: ExtractionDiffAddedItem[];
  updated: ExtractionDiffUpdatedItem[];
  removed: ExtractionDiffRemovedItem[];
  warnings: ExtractionWarning[];
};

/**
 * 差分のフロントエンド内部型
 */
export type ExtractionDiff = {
  added: ExtractionDiffAddedItem[];
  updated: ExtractionDiffUpdatedItem[];
  removed: ExtractionDiffRemovedItem[];
  warnings: ExtractionWarning[];
};

// ========================================
// 適用（apply）関連
// ========================================

/**
 * POST .../extraction-sources/apply リクエスト
 */
export type ApplyExtractionApiRequest = {
  executionRunPublicId: string;
};

/**
 * POST .../extraction-sources/apply レスポンス（openapi ApplyExtractionResponse 準拠）
 */
export type ApplyExtractionApiResponse = {
  applied: true;
  addedCaseIds: string[];
  updatedCaseIds: string[];
  removedCaseIds: string[];
  warnings: ExtractionWarning[];
};

/**
 * 適用結果のフロントエンド内部型
 */
export type ApplyExtractionResult = {
  applied: true;
  addedCaseIds: string[];
  updatedCaseIds: string[];
  removedCaseIds: string[];
  warnings: ExtractionWarning[];
};

// ========================================
// parse
// ========================================

/**
 * API レスポンスから ExtractionDiff 内部型へ変換
 */
export const parseExtractionDiff = (
  r: ExtractionDiffApiResponse,
): ExtractionDiff => ({
  added: r.added,
  updated: r.updated,
  removed: r.removed,
  warnings: r.warnings,
});

/**
 * API レスポンスから ApplyExtractionResult 内部型へ変換
 */
export const parseApplyExtractionResult = (
  r: ApplyExtractionApiResponse,
): ApplyExtractionResult => ({
  applied: r.applied,
  addedCaseIds: r.addedCaseIds,
  updatedCaseIds: r.updatedCaseIds,
  removedCaseIds: r.removedCaseIds,
  warnings: r.warnings,
});

/** 人件費総括表（抽出ソース）の許可MIMEタイプ値一覧（単一ソース） */
export const EXTRACTION_SOURCE_CONTENT_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
] as const;

/** 人件費総括表（抽出ソース）の許可MIMEタイプ */
export type ExtractionSourceContentType =
  (typeof EXTRACTION_SOURCE_CONTENT_TYPES)[number];

/** アップロードステータス */
export type SourceFileUploadStatus =
  | 'idle'
  | 'uploading'
  | 'uploaded'
  | 'failed';

/** OCRステータス */
export type SourceFileOcrStatus =
  | 'idle'
  | 'processing'
  | 'completed'
  | 'failed';

// ========================================
// HTTP リクエスト/レスポンス
// ========================================

/** GET /api/v0/tenants/{tenantId}/projects/{projectId}/extraction-sources レスポンス */
export type ExtractionSourceListApiResponse =
  PaginatedResponse<SourceFileApiResponse>;

/** ExtractionSourceListApiResponse の items 要素 */
export type SourceFileApiResponse = {
  publicId: string;
  fileName: string;
  fileHash: string;
  uploadStatus: SourceFileUploadStatus;
  ocrStatus: SourceFileOcrStatus;
  /**
   * OCR 失敗理由（Issue #475 Phase 3）。`ocrStatus='failed'` 以外は null。
   *
   * openapi 契約上は必ず載る（nullable）が、Phase 3 以前に作られた固定データ /
   * mock ではキー自体が無いことがあるため optional で受け、parse で null へ正規化する。
   */
  ocrFailedReason?: OcrFailedReason | null;
  createdAt: string;
  updatedAt: string;
};

/** POST /api/v0/tenants/{tenantId}/projects/{projectId}/extraction-sources/upload-url リクエスト */
export type SourceFileUploadUrlApiRequest = {
  /** ファイル名（.xlsx / .xls のみ） */
  fileName: string;
  /** SHA-256 ハッシュ */
  fileHash: string;
  /** MIMEタイプ（.xlsx: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet / .xls: application/vnd.ms-excel） */
  contentType: ExtractionSourceContentType;
};

/** POST /api/v0/tenants/{tenantId}/projects/{projectId}/extraction-sources/upload-url レスポンス */
export type SourceFileUploadUrlApiResponse = {
  publicId: string;
  fileName: string;
  /** S3 署名付き URL */
  uploadUrl: string;
  /** URL有効期限（秒） */
  expiresIn: number;
};

// ========================================
// フロントエンド内部型
// ========================================

export type SourceFile = {
  publicId: string;
  fileName: string;
  fileHash: string;
  uploadStatus: SourceFileUploadStatus;
  ocrStatus: SourceFileOcrStatus;
  /**
   * OCR 失敗理由（Issue #475 Phase 3、Issue #550）。
   *
   * 4 状態を持つ:
   *   - `usage_limit_exceeded`: AI 利用上限に到達して OCR を実行できなかった
   *   - `ai_server_error`:      AI サーバー起因のエラーで OCR に失敗した
   *   - `conversion_error`:     PDF 変換に失敗して抽出が始まらなかった（Issue #550）
   *   - `null`:                 理由不明（Phase 3 以前の古いデータ / OCR 失敗していない）
   *
   * 応答の値を落とさず運ぶための保持で、**一覧の表示側は未接続**
   * （総括表の失敗表示は Project の `extractionFailedReason` を使う。Phase 5.5）。
   */
  ocrFailedReason: OcrFailedReason | null;
  createdAt: Date;
  updatedAt: Date;
};

export type SourceFileUploadUrlRequest = {
  fileName: string;
  fileHash: string;
  contentType: ExtractionSourceContentType;
};

export type SourceFileUploadUrlResponse = {
  publicId: string;
  fileName: string;
  uploadUrl: string;
  expiresIn: number;
};

// ========================================
// parse
// ========================================

export const parseSourceFile = (s: SourceFileApiResponse): SourceFile => ({
  publicId: s.publicId,
  fileName: s.fileName,
  fileHash: s.fileHash,
  uploadStatus: s.uploadStatus,
  ocrStatus: s.ocrStatus,
  // 未知の値・キー欠落は null（理由不明）へ正規化する（上限到達に寄せない）
  ocrFailedReason: parseOcrFailedReason(s.ocrFailedReason),
  createdAt: new Date(s.createdAt),
  updatedAt: new Date(s.updatedAt),
});

export const parseSourceFileUploadUrlResponse = (
  r: SourceFileUploadUrlApiResponse,
): SourceFileUploadUrlResponse => ({
  publicId: r.publicId,
  fileName: r.fileName,
  uploadUrl: r.uploadUrl,
  expiresIn: r.expiresIn,
});
